

import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDesignations } from "../../redux/actions/designationActions";
import { fetchOffices } from "../../redux/actions/officeActions";
import { fetchShifts } from "../../redux/actions/shiftActions";
import theme from "../../components/shared/theme";
import {
  modalOverlayStyle,
  modalContentStyle,
  closeButtonStyle,
} from "../../components/shared/themeStyles";

const EditEmployeeModal = ({ employee, onChange, onCancel, onSubmit }) => {
  const dispatch = useDispatch();
  
  // Get data from Redux store
  const { designations } = useSelector((state) => state.designation);
  const { offices } = useSelector((state) => state.office);
  const { shifts } = useSelector((state) => state.shift);

  useEffect(() => {
    // Fetch all required data
    dispatch(fetchDesignations());
    dispatch(fetchOffices());
    dispatch(fetchShifts());
  }, [dispatch]);

  if (!employee) return null;

  // Custom styles for better appearance
  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    border: `1px solid ${theme.colors.grayLight}`,
    borderRadius: theme.radius.md,
    fontSize: theme.font.size.md,
    transition: "all 0.3s ease",
    backgroundColor: "#fff",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "6px",
    fontSize: theme.font.size.sm,
    fontWeight: theme.font.weight.medium,
    color: theme.colors.grayDark,
  };

  const selectStyle = {
    ...inputStyle,
    appearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%23333' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14L2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 16px center",
    backgroundSize: "12px",
    paddingRight: "40px",
  };

  const buttonStyle = (primary = true) => ({
    padding: "12px 24px",
    borderRadius: theme.radius.md,
    border: "none",
    fontSize: theme.font.size.md,
    fontWeight: theme.font.weight.semibold,
    cursor: "pointer",
    transition: "all 0.3s ease",
    minWidth: "100px",
    ...(primary ? {
      backgroundColor: theme.colors.primary,
      color: "#fff",
      "&:hover": {
        backgroundColor: theme.colors.primaryDark,
        transform: "translateY(-1px)",
      },
    } : {
      backgroundColor: theme.colors.grayLight,
      color: theme.colors.grayDark,
      "&:hover": {
        backgroundColor: theme.colors.gray,
        transform: "translateY(-1px)",
      },
    }),
  });

  const formatTime = (timeString) => {
    if (!timeString) return "";
    // Convert "HH:MM:SS" to "HH:MM AM/PM"
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minutes} ${ampm}`;
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={{
        ...modalContentStyle,
        maxWidth: "600px",
        width: "90%",
        maxHeight: "90vh",
        overflowY: "auto",
        padding: theme.spacing.lg,
      }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: theme.spacing.lg,
          paddingBottom: theme.spacing.md,
          borderBottom: `1px solid ${theme.colors.grayLight}`,
        }}>
          <h2 style={{
            margin: 0,
            fontSize: theme.font.size.xl,
            fontWeight: theme.font.weight.bold,
            color: theme.colors.grayDark,
          }}>
            Edit Employee
          </h2>
          <button 
            onClick={onCancel} 
            style={{
              ...closeButtonStyle,
              width: "32px",
              height: "32px",
              fontSize: "20px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            &times;
          </button>
        </div>

        <form onSubmit={onSubmit}>
          <div style={{ display: "grid", gap: theme.spacing.lg }}>
            {/* Personal Information Section */}
            <div>
              <h3 style={{
                marginBottom: theme.spacing.md,
                fontSize: theme.font.size.lg,
                fontWeight: theme.font.weight.semibold,
                color: theme.colors.grayDark,
              }}>
                Personal Information
              </h3>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: theme.spacing.md,
              }}>
                <div>
                  <label style={labelStyle}>Full Name *</label>
                  <input
                    style={inputStyle}
                    type="text"
                    value={employee?.name || ""}
                    onChange={(e) => onChange("name", e.target.value)}
                    placeholder="Enter full name"
                    required
                  />
                </div>
                <div>
                  <label style={labelStyle}>Email Address *</label>
                  <input
                    style={inputStyle}
                    type="email"
                    value={employee?.email || ""}
                    onChange={(e) => onChange("email", e.target.value)}
                    placeholder="Enter email address"
                    required
                  />
                </div>
                <div>
                  <label style={labelStyle}>Phone Number *</label>
                  <input
                    style={inputStyle}
                    type="tel"
                    value={employee?.phone || ""}
                    onChange={(e) => onChange("phone", e.target.value)}
                    placeholder="Enter phone number"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Employment Details Section */}
            <div>
              <h3 style={{
                marginBottom: theme.spacing.md,
                fontSize: theme.font.size.lg,
                fontWeight: theme.font.weight.semibold,
                color: theme.colors.grayDark,
              }}>
                Employment Details
              </h3>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: theme.spacing.md,
              }}>
                <div>
                  <label style={labelStyle}>Date of Birth</label>
                  <input
                    style={inputStyle}
                    type="date"
                    value={employee.dob?.split("T")[0] || ""}
                    onChange={(e) => onChange("dob", e.target.value)}
                    max={new Date().toISOString().split("T")[0]}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Date of Joining</label>
                  <input
                    style={inputStyle}
                    type="date"
                    value={employee.doj?.split("T")[0] || ""}
                    onChange={(e) => onChange("doj", e.target.value)}
                    max={new Date().toISOString().split("T")[0]}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Designation *</label>
                  <select
                    style={selectStyle}
                    value={employee.designation || ""}
                    onChange={(e) => onChange("designation", e.target.value)}
                    required
                  >
                    <option value="">Select Designation</option>
                    {Array.isArray(designations) && designations.map((designation) => (
                      <option key={designation.id || designation.name} value={designation.name}>
                        {designation.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Work Schedule Section */}
            <div>
              <h3 style={{
                marginBottom: theme.spacing.md,
                fontSize: theme.font.size.lg,
                fontWeight: theme.font.weight.semibold,
                color: theme.colors.grayDark,
              }}>
                Work Schedule
              </h3>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: theme.spacing.md,
              }}>
                <div>
                  <label style={labelStyle}>Office Location *</label>
                  <select
                    style={selectStyle}
                    value={employee?.office_id || ""}
                    onChange={(e) => onChange("office_id", parseInt(e.target.value))}
                    required
                  >
                    <option value="">Select Office</option>
                    {Array.isArray(offices) && offices.map((office) => (
                      <option key={office.id} value={office.id}>
                        {office.office_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Work Shift *</label>
                  <select
                    style={selectStyle}
                    value={employee?.shift_id || ""}
                    onChange={(e) => onChange("shift_id", parseInt(e.target.value))}
                    required
                  >
                    <option value="">Select Shift</option>
                    {Array.isArray(shifts) && shifts.map((shift) => (
                      <option key={shift.id} value={shift.id}>
                        {shift.name} ({formatTime(shift.start_time)} - {formatTime(shift.end_time)})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            marginTop: theme.spacing.xl,
            paddingTop: theme.spacing.lg,
            borderTop: `1px solid ${theme.colors.grayLight}`,
            display: "flex",
            justifyContent: "flex-end",
            gap: theme.spacing.md,
          }}>
            <button
              type="button"
              onClick={onCancel}
              style={buttonStyle(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={buttonStyle(true)}
            >
              Update Employee
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEmployeeModal;